import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import browserHistoryEntries from '~/services/BrowserHistoryEntries';
import navigationHistory from '~/services/NavigationHistory';

// Long enough for a redirect chain to settle, so the page it passed through is
// not counted as a view of its own.
const PAGE_VIEW_DEBOUNCE_MS = 150;

export const useNavigationTracking = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    navigationHistory.push(`${pathname}${search}`);
    browserHistoryEntries.record(pathname);
  }, [pathname, search]);

  useEffect(() => {
    analyticsService.track(AnalyticsEvent.PAGE_VIEW_BI, {
      path: pathname,
      title: document.title,
    });
    if (!pathname.includes('/p')) {
      analyticsService.track(AnalyticsEvent.PIXEL_PAGE_VIEW, {});
    }
  }, [pathname]);

  // Metrika's tag has no History API listener, so a client-side navigation is
  // invisible to it unless reported by hand. No title: on a product or category
  // page it is written once the data arrives, so any title captured here would
  // still be the previous page's.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      analyticsService.track(AnalyticsEvent.PAGE_VIEW_YM, {
        url: `${window.location.origin}${pathname}`,
      });
    }, PAGE_VIEW_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [pathname]);

  return {
    canGoBack: navigationHistory.canGoBack(),
    canGoForward: navigationHistory.canGoForward(),
    getPreviousPath: () => navigationHistory.getPreviousPath(),
    getCurrentPath: () => navigationHistory.getCurrentPath(),
    getHistoryStack: () => navigationHistory.getHistoryStack(),
  };
};
